<template>
  <div class="layers">
    <div class="layers-headers">
      <p class="menu-title">
        Layers
      </p>
      <LayerCatalog
        v-if="hasLayersCatalog"
        :disabled="dataLoading"
      />
    </div>
    <div
      v-if="dataLoading"
      class="center-icon"
    >
      <LoadIcon :size="80" />
    </div>
    <div
      v-else
      class="layers-container"
    >
      <span v-if="userLayers.length === 0">
        No layers selected - click "More Layers" to add and remove layers
      </span>
      <LayerOption
        v-for="layer in userLayers"
        :key="layer.id"
        :layer="layer"
        :current-zoom="currentZoom"
        :total-usage="layerUsage(layer.id)"
        @toggle-checked="(unmounting) => toggleLayerChecked(layer, unmounting)"
        @toggle-expanded="toggleLayerExpanded(layer)"
        @selected-option="selectedOption(layer, $event)"
        @save-fill-opacity="updateUserLayer({id: layer.id, fillOpacity: $event})"
      >
        <template #legendBody>
          <VerticalLegend
            v-if="layer.legendStyle === 'vertical' && (layer?.legendItems || layer?.legendGroups)"
            :layer="layer"
          />
          <HorizontalLegend
            v-else-if="layer.legendStyle === 'horizontal' && layer?.labels?.length && layer?.colors?.length"
            :labels="layer.labels"
            :colors="layer.colors"
          />
        </template>
        <template
          v-if="['metro', 'longhaul'].includes(layer.id)"
          #extraData
        >
          <FiberLayerUsage :layer="layer" />
        </template>
        <template
          v-else-if="layer.id === 'drone'"
          #extraData
        >
          <SurveyDropdown :layer="layer" />
        </template>
        <template
          v-else-if="layer.hasFilters"
          #extraData
        >
          <LayerFilterList :layer="layer" />
        </template>
      </LayerOption>
    </div>
  </div>
</template>

<script>
// Services and Helpers
import {MapService} from '@/services/MapService';
import {mapActions, mapState} from 'vuex';
import {createAuditRecord} from '@/services/BackendService';
import {getCollection} from '@/lib/filters/filters.js';

// Components
import LayerOption from './sections/LayerOption.vue';
import HorizontalLegend from './sections/Legend/HorizontalLegend.vue';
import VerticalLegend from './sections/Legend/VerticalLegend.vue';
import LoadIcon from '../svgs/LoadIcon.vue';
import SurveyDropdown from './sections/SurveyDropdown.vue';
import FiberLayerUsage from './sections/FiberLayerUsage.vue';
import LayerCatalog from './sections/LayerCatalog.vue';
import LayerFilterList from './sections/LayerFilterList.vue';

/** @typedef {import('@/services/GraphicService/MapLayer').MapLayer} MapLayer */

export default {
  name: 'LayersMenu',
  components: {
    LayerOption,
    HorizontalLegend,
    VerticalLegend,
    LoadIcon,
    SurveyDropdown,
    FiberLayerUsage,
    LayerCatalog,
    LayerFilterList,
  },
  data() {
    return {
      dataLoading: true,
      currentZoom: 0,
      timeout: 2000, // Milliseconds
      surveys: [],
      previousZoom: 0,
    };
  },
  computed: {
    ...mapState(['selectedOverlay', 'dateFormatter_US', 'sharedMap', 'hasLayersCatalog']),
    ...mapState('layers', ['usage', 'userLayers']),
    map: () => MapService.getMap(),
  },
  async mounted() {
    await this.loadLayers();

    this.currentZoom = Math.floor(this.map.getZoom());
    this.map.addListener('zoom_changed', () => {
      const zoomChanged = Math.floor(this.map.getZoom()) !== this.previousZoom;
      if (!zoomChanged) return; // Only re-evaluate the layer if the zoom integer has changed

      this.currentZoom = Math.floor(this.map.getZoom());
      this.userLayers.forEach((layer) => {
        // If current zoom is in between max and min exclusively, the layer is shown
        // and doesn't need to be recalculated unless the the viewport has zoomed in past the
        // layer's minZoom. This prevents issues with NP Tax Parcel overlays not loading when
        // panning via search
        const layerAlreadyVisible = this.currentZoom < layer.maxZoom && this.currentZoom > layer.minZoom;
        const recalcLayer = this.previousZoom < layer.minZoom;

        if (layerAlreadyVisible && !recalcLayer) return;
        if (layer.checked && !layer.zoomOk(layer)) {
          this.hideLayer(layer);
        } else if (layer.checked && layer.zoomOk(layer)) {
          this.showLayer(layer);
        }
      });
      this.previousZoom = this.currentZoom;
    });
  },
  methods: {
    ...mapActions('layers', ['fetchLayers', 'fetchUsage', 'fetchUserLayers', 'updateUserLayer']),
    async loadLayers() {
      const promises = [this.fetchLayers()];
      if (this.fetchUsage) {
        promises.push(this.fetchUsage());
      }
      const results = await Promise.allSettled(promises);
      results.forEach((res) => res.status === 'rejected' && console.error(res.reason));

      await this.fetchUserLayers();

      this.currentZoom = MapService.getMap().getZoom();
      // Show layers that are checked and in range at the current zoom level
      this.userLayers.forEach(async (layer) => {
        if (layer.checked) {
          await this.showLayer(layer);
          this.toggleLayerExpanded(layer);
        }
      });
      this.previousZoom = this.currentZoom;

      this.dataLoading = false;
    },
    /**
     * When a new option is selected, hide any currently displayed graphics for the layer then show the graphics
     * for the new selection.
     * @param {Object} layer
     * @param {any} optionValue
     */
    selectedOption(layer, optionValue) {
      layer.setOption(optionValue);
      this.updateUserLayer({
        id: layer.id,
        options: layer.options.map((option) => ({
          checked: option.label === optionValue,
        })),
      });
    },
    /**
     * When the checkbox is toggled, handle expanding the layer items and showing/hiding the layer.
     * @param {Object} layer
     * @param {boolean} unmounting - if the method is called from LayerOption unmount
     */
    toggleLayerChecked(layer, unmounting = false) {
      layer.checked = !layer.checked;
      // Persist the layer's checked state for the user
      // Don't persist the state when the layers menu unmounts when navigating between shared maps
      if (!unmounting) {
        this.updateUserLayer({
          id: layer.id,
          selected: layer.checked,
        });
      }

      layer.expanded = layer.checked;
      if (layer.checked) {
        this.showLayer(layer);
        createAuditRecord(`layerOn-${layer.id}`);
      } else {
        this.hideLayer(layer);
        createAuditRecord(`layerOff-${layer.id}`);
        // This layer is unique in that it handles rendering its own features using a map listener.
        // We don't want to remove the listener if the layer is hidden due to zoom level.
        // We only want to remove the listener and force items to be removed if the layer is unchecked.
        if (layer.type === 'taxParcel') {
          const taxFeatures = getCollection('taxFeatures').getSource();
          taxFeatures.forEach((feature) => {
            // don't hide features that are part of currently selected overlays
            if (this.selectedOverlay?.features?.has(feature.id)) return;
            feature.setRender(false);
          });
          MapService.removeMapListener('taxParcelIdle');
        }
      }
    },
    /**
     * When the layer is clicked, handle expanding the layer items.
     * @param {Object} layer
     */
    toggleLayerExpanded(layer) {
      layer.expanded = !layer.expanded;
      if (!layer.checked) {
        this.toggleLayerChecked(layer);
      }
    },
    /**
     * @param {MapLayer} layer
     */
    async showLayer(layer) {
      if (!layer.zoomOk()) return;
      try {
        await layer.initialize(this.map);
        layer.setMap(this.map);
      } catch (error) {
        console.error(error);
      }
    },
    /**
     * @param {MapLayer} layer
     */
    hideLayer(layer) {
      try {
        layer.setMap(null);
      } catch (error) {
        console.error(error);
      }
    },
    layerUsage(layerId) {
      if (layerId === 'metro' || layerId === 'longhaul') {
        return this.usage?.find((usage) => usage.label === 'fiberLocator')?.tiles;
      }
      return null;
    },
  },
};
</script>
<style lang="scss" scoped>
.center-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5rem 0;
}
.layers {
  @include main.respond(desktop) {
    padding: 27px 25px !important;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: visible !important;
  }

  &-headers {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &-container {
    padding-top: 13px;
    display: flex;
    flex-direction: column;
    @include main.respond(desktop) {
      flex: 1;
      min-height: 0;
      margin-right: -25px;
      padding-right: 25px;
      overflow-y: auto;
      overflow-x: hidden;
    }
    p {
      /* Forced to the same height as the input */
      margin-bottom: 1rem;
      border: 1px solid transparent;
      height: 32px;
      font-size: 14px;
    }
  }
}
</style>
